import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

/**
 * FileDropzone — reusable drag-and-drop / click-to-select file input.
 *
 * @description
 *   Thin wrapper around react-dropzone's getRootProps/getInputProps with the
 *   token-correct dashed-border drop box. Extracted from FulfillmentUpload so
 *   any flow that needs a "drag here or click" zone reuses one styled, a11y
 *   surface instead of re-inlining the dropzone. Always calls back with the
 *   accepted-files ARRAY — consumers append it (multi) or take files[0]
 *   (single). Visual-only theming via theme tokens; no color literals.
 *
 * @useWhen
 *   - reusable drag-and-drop file input
 *   - click-or-drop upload zone
 *   - a small drop target inside a larger form (upload modal, CSV import)
 *   - need keyboard-focusable, click-to-select file picking
 *   - a DROP-ONLY surface around your own button (`noClick` + `noKeyboard` +
 *     `children`): the zone takes a drop, your button opens the file dialog
 *   - a drop surface that changes its own copy while dragging — pass `children`
 *     as a function and it receives `{ isDragActive }`
 *
 * @dontUseFor
 *   - rich-text inline paste / editor drops → SmartCompose DropZoneOverlay
 *   - full upload widget with progress + file list → compose around this
 *   - page-wide drop overlay → build a page-level overlay (MediaLibraryPage)
 *
 * 🔴 **The click promise holds only WITHOUT `noClick`; the keyboard promise only
 *   WITHOUT `noKeyboard`.** `noClick` takes ONLY the click-to-select away — the
 *   tab stop STAYS, because `getRootProps()` sets `tabIndex: 0` whenever
 *   `noKeyboard` is unset. `noKeyboard` is the one that removes the `tabindex`
 *   (the attribute is ABSENT, not `-1`). With both, the caller's own control is
 *   the only way in.
 *
 * 🔴 **With BOTH flags the zone renders NO `<input type="file">` at all.** The
 *   drop path lives on the box (react-dropzone wires `onDrop` to the root, the
 *   input only serves the dialog), so a drop-only surface has no use for the
 *   field — and a merely hidden one still appears in the accessibility tree as a
 *   second, dead "Choose file" button. `noClick` alone keeps the field.
 *
 * @related
 *   - FulfillmentUpload — composes this for fulfillment CSV imports
 *   - SmartCompose/extensions/DropZoneOverlay — Tiptap editor drop surface
 *
 * @reuseHints
 *   - MediaUploadModal file tab (onFiles={f => setFile(f[0])})
 *   - PO invoice / attachment upload
 *   - any vendor/product form that accepts a file
 *
 * @props
 *   onFiles   — REQUIRED (files: File[]) => void; always the accepted ARRAY
 *   accept    — optional react-dropzone accept object (default any)
 *   multiple  — optional bool, default false
 *   label     — optional main text line
 *   hint      — optional secondary caption line (e.g. file-type hint)
 *   disabled  — optional bool
 *   noClick    — optional bool, default false; takes ONLY the click-to-select
 *                away — the tab stop STAYS (that is `noKeyboard`'s job).
 *                Together with `noKeyboard` it also removes the hidden
 *                `<input type="file">` from the DOM.
 *   noKeyboard — optional bool, default false; the box is not focusable at all
 *                (no `tabindex`), and Enter/Space on it do nothing
 *   onRejected — optional (rejections) => void; react-dropzone's rejection
 *                list (e.g. `too-many-files` when two files hit `multiple:false`).
 *                Without it nothing is reported, as before.
 *   children  — optional node, or a function `({ isDragActive }) => node`,
 *                replacing icon, `label` and `hint` (the drop surface stays the
 *                same box). The function form lets the caller carry the drag
 *                state in TEXT — status is never colour alone (R16)
 *   sx        — optional style override merged into the Box
 *   ...rest   — any other prop lands on the BOX after `getRootProps()`
 *                (e.g. `data-testid`), so it hits the drop surface. 🔴 Do NOT
 *                pass drag/click handlers here (`onClick`, `onDrop`,
 *                `onKeyDown`): they are spread LAST and would silently
 *                override the zone's own wiring
 */
export default function FileDropzone({
  onFiles,
  accept,
  multiple = false,
  label = 'Drag & drop files here, or click to select',
  hint,
  disabled = false,
  noClick = false,
  noKeyboard = false,
  onRejected,
  children,
  sx,
  ...rest
}) {
  const onDrop = useCallback((accepted, abweisungen) => {
    onFiles(accepted);
    // 🔴 Only when someone listens: the three existing callers keep their exact
    //    behaviour (no second callback, no new branch).
    if (onRejected && abweisungen && abweisungen.length) onRejected(abweisungen);
  }, [onFiles, onRejected]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    disabled,
    noClick,
    noKeyboard,
  });

  // 🔴 Measured on react-dropzone 14.3.8, and re-measured here: with `noKeyboard`
  //    `getRootProps()` already leaves `tabIndex` out (the attribute is ABSENT,
  //    not `-1`), with the flag unset it is `0`. The line below makes that
  //    promise the component's own instead of the library's: should a version
  //    ever return `-1`, the box would become click-focusable — exactly what
  //    `noKeyboard` must prevent. Without the flag the measured value passes
  //    through unchanged.
  const { tabIndex, ...rootProps } = getRootProps();

  // 🔴 Drop-only (`noClick` UND `noKeyboard`) rendert KEIN `<input>`.
  //    Gemessen an react-dropzone 14.3.8 (`dist/es/index.js`): der Ablageweg
  //    haengt am Wurzelelement — `getRootProps()` setzt dort
  //    `onDrop: composeDragHandler(composeEventHandlers(onDrop, onDropCb))`
  //    (Zeile 878), und `onDropCb` (Zeile 720) ruft `setFiles` → `onDrop`.
  //    `getInputProps()` traegt nur `onChange` (Zeile 913) fuer den Dialogweg.
  //    Ohne Klick und ohne Tastatur oeffnet die Box den Dialog nie, der Aufrufer
  //    bringt seinen eigenen Knopf und sein eigenes Feld mit — das Feld der Zone
  //    ist dann tot, steht aber im Barrierefrei-Baum: im Dialog „Upload export
  //    proof" fand ein Screenreader ZWEI Knoepfe „Choose file", einen davon ohne
  //    Wirkung. Deshalb hier weglassen statt verstecken.
  //    Nur `noClick` allein behaelt das Feld: dort ist es der Bestandsweg.
  const nurAblage = noClick && noKeyboard;

  return (
    <Box
      {...rootProps}
      {...rest}
      tabIndex={noKeyboard ? undefined : tabIndex}
      sx={{
        p: 3,
        border: '2px dashed',
        borderColor: isDragActive ? 'primary.main' : 'divider',
        borderRadius: 2,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : (noClick ? 'default' : 'pointer'),
        bgcolor: isDragActive ? 'action.hover' : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
        opacity: disabled ? 0.6 : 1,
        userSelect: 'none',
        ...sx,
      }}
    >
      {nurAblage ? null : <input {...getInputProps()} />}
      {/* 🔴 Eine Funktion als `children` bekommt `{ isDragActive }` und darf
          damit den Ziehen-Zustand in TEXT tragen (Status nie nur ueber Farbe,
          R16). Ein Knoten und ein fehlendes `children` rendern wie bisher —
          die drei Bestandsaufrufer sind byte-gleich. */}
      {typeof children === 'function'
        ? children({ isDragActive })
        : children || (
          <>
            <CloudUploadIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.disabled">
                {hint}
              </Typography>
            )}
          </>
        )}
    </Box>
  );
}
